import { baseApi } from "@/shared/api";
import type { OCRResponse } from "@/features/accounting/warehouse";
import type { CreatePurchaseDto, InvoiceDetailResponse, InvoiceListResponse, UpdatePurchaseDto, PatchLineItemDto, ApprovePurchaseBody } from "../types";

export const purchasesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInvoices: builder.query<
      InvoiceListResponse,
      {
        transaction_status?: string;
        vendor?: number;
        start_date?: string;
        end_date?: string;
        category?: string;
        search?: string;
        ordering?: string;
        page?: number;
        page_size?: number;
      } | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.transaction_status) searchParams.append("transaction_status", params.transaction_status);
        if (params?.vendor) searchParams.append("vendor", String(params.vendor));
        if (params?.start_date) searchParams.append("start_date", params.start_date);
        if (params?.end_date) searchParams.append("end_date", params.end_date);
        if (params?.category) searchParams.append("category", params.category);
        if (params?.search) searchParams.append("search", params.search);
        if (params?.ordering) searchParams.append("ordering", params.ordering);
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.page_size) searchParams.append("page_size", String(params.page_size));
        const queryString = searchParams.toString();
        return {
          url: queryString ? `/api/warehouse/invoices/?${queryString}` : `/api/warehouse/invoices/`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: [{ type: "Purchases" as const, id: "LIST" }],
    }),
    getInvoiceDetail: builder.query<InvoiceDetailResponse, number>({
      query: (id) => ({
        url: `/api/warehouse/invoices/${id}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id: number) => [{ type: "Purchases" as const, id }],
    }),
    createPurchase: builder.mutation<InvoiceDetailResponse, CreatePurchaseDto>({
      query: (body) => ({
        url: `/api/warehouse/invoices/receive/`,
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [
        { type: "Purchases" as const, id: "LIST" },
        { type: "InventoryItems" as const, id: "VENDORS_LIST" },
      ],
    }),
    extractOCR: builder.mutation<OCRResponse, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/api/warehouse/ocr/extract/`,
          method: "POST",
          body: formData,
          credentials: "include",
        };
      },
    }),
    approvePurchase: builder.mutation<InvoiceDetailResponse, { id: number; body?: ApprovePurchaseBody }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/invoices/${id}/approve/`,
        method: "POST",
        body: body || {},
        credentials: "include",
      }),
      invalidatesTags: (_result: InvoiceDetailResponse | undefined, _error: unknown, { id }: { id: number }) => [
        { type: "Purchases" as const, id: "LIST" },
        { type: "Purchases" as const, id },
        "SmetaData",
        "Budget",
      ],
    }),
    markPaidPurchase: builder.mutation<InvoiceDetailResponse, { id: number; paid_date?: string }>({
      query: ({ id, paid_date }) => ({
        url: `/api/warehouse/invoices/${id}/mark-paid/`,
        method: "POST",
        body: paid_date ? { paid_date } : {},
        credentials: "include",
      }),
      invalidatesTags: (_result: InvoiceDetailResponse | undefined, _error: unknown, { id }: { id: number }) => [
        { type: "Purchases" as const, id: "LIST" },
        { type: "Purchases" as const, id },
      ],
    }),
    updatePurchase: builder.mutation<InvoiceDetailResponse, { id: number; body: UpdatePurchaseDto }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/invoices/${id}/`,
        method: "PUT",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result: InvoiceDetailResponse | undefined, _error: unknown, { id }: { id: number }) => [
        { type: "Purchases" as const, id: "LIST" },
        { type: "Purchases" as const, id },
      ],
    }),
    partialUpdatePurchase: builder.mutation<InvoiceDetailResponse, { id: number; body: UpdatePurchaseDto }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/invoices/${id}/`,
        method: "PATCH",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result: InvoiceDetailResponse | undefined, _error: unknown, { id }: { id: number }) => [
        { type: "Purchases" as const, id: "LIST" },
        { type: "Purchases" as const, id },
      ],
    }),
    deletePurchase: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/warehouse/invoices/${id}/`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: [{ type: "Purchases" as const, id: "LIST" }],
    }),
    patchLineItem: builder.mutation<InvoiceDetailResponse, { invoiceId: number; lineItemId: number; body: PatchLineItemDto }>({
      query: ({ invoiceId, lineItemId, body }) => ({
        url: `/api/warehouse/invoices/${invoiceId}/line-items/${lineItemId}/`,
        method: "PATCH",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "Purchases" as const, id: "LIST" },
        { type: "Purchases" as const, id: invoiceId },
      ],
    }),
  }),
});

export const {
  useGetInvoicesQuery,
  useGetInvoiceDetailQuery,
  useLazyGetInvoiceDetailQuery,
  useCreatePurchaseMutation,
  useExtractOCRMutation,
  useApprovePurchaseMutation,
  useMarkPaidPurchaseMutation,
  useUpdatePurchaseMutation,
  usePartialUpdatePurchaseMutation,
  useDeletePurchaseMutation,
  usePatchLineItemMutation,
} = purchasesApi;
